import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  ReactNode,
} from "react";
import { useLocation, useRouteMatch, useHistory } from "react-router-dom";
import { STORE_INFO_MAPPING, STORE_INFO_USA } from "./mapping";

// @todo this is the worst cookie library, no TS, just using because it is installed
import cookies from "react-cookies";

import { allRoutes } from "./routes";
import {
  CountryCode,
  StoreId,
  Language,
  Nullish,
} from "@ubnt/store.core/lib/types";
import { REGIONAL_MICROSITE_PREFIX_PATH } from "constants/routes";
import {
  evalCountryCode,
  evalStoreId,
  evalLanguageCode,
  saveCookie,
} from "./utils";
import {
  pathnameToParts,
  partsToPathname,
} from "@ubnt/store.core/lib/utils/url";
import { useEffectOnce } from "react-use";
import {
  DEFAULT_COUNTRY_CODE,
  DEFAULT_LANGUAGE,
  GLOBAL_SITE_CONFIG,
  UISP_SITE_CONFIG,
} from "./constants";
import type { SiteSettingsContextValue } from "./types";

/**
 * Changed from "country-code" to "country-code-session" to solve issue that was caused
 * with microsite language cookie PR where the country code was set to expire in 1 year
 * instead of session.
 */
export const COOKIE_COUNTRY_CODE = "country-code-session";
export const COOKIE_MICROSITE_LANGUAGE = "microsite-language";

export const LocalStorageKey = {
  StoreReferer: "store-referer",
  LanguageReferer: "language-referer",
};

export const ModalId = {
  Support: "support",
  Account: "account",
  MobileMenu: "mobileMenu",
};

export const SiteSettings = createContext<SiteSettingsContextValue | null>(
  null,
);

export const SiteSettingsProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const history = useHistory();

  // #############################################################################
  // Geo-IP detection
  // #############################################################################
  /**
   * null - Not processed
   * undefined - Geo-IP detection failed
   * CountryCode - Geo-IP detection succeeded
   */
  const [geoCountryCode, setGeoCountryCode] =
    useState<Nullish<CountryCode>>(null);

  useEffect(() => {
    // Cookies
    const cookieCountryCode = evalCountryCode(
      cookies.load(COOKIE_COUNTRY_CODE),
    );

    if (cookieCountryCode) {
      setGeoCountryCode(cookieCountryCode);

      // Exit early if we already have stored value
      return;
    }

    let controller = null;
    const runAsync = async () => {
      try {
        controller = new AbortController();

        const url = new URL(GLOBAL_SITE_CONFIG.store.defaultRegion.baseUrl);
        url.pathname = "/api/geo-ip";

        const res = await fetch(url.toString(), {
          mode: "cors",
          signal: controller.signal,
          // @ts-ignore Semi new option (not supported on all browsers) - https://developer.mozilla.org/en-US/docs/Web/API/fetch#browser_compatibility
          priority: "high",
        });
        const geoResult = await res.json();

        // const geoStoreId = geoResult?.preferredStoreId;
        const geoCountry = geoResult?.country;

        const detectedCountryCode = evalCountryCode(geoCountry);

        // !!! Save as session cookie (no expiration date)
        cookies.save(COOKIE_COUNTRY_CODE, detectedCountryCode, {
          path: "/",
          domain: ".ui.com",
          expires: undefined,
          secure: true,
          sameSite: "lax",
        });

        setGeoCountryCode(detectedCountryCode);
      } catch (err) {
        // Silence errors
        console.error("Failed to run geo-location", err);
      }
    };

    runAsync();

    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, []);

  // #############################################################################
  // Country code
  // #############################################################################
  const countryCode = useMemo(() => {
    /**
     * If geoCountryCode is null, it means that the geo-ip detection is not yet
     * processed. If it is undefined, it means that the geo-ip detection failed.
     * In both cases, we want to fallback to the default country code.
     */
    return geoCountryCode ?? DEFAULT_COUNTRY_CODE;
  }, [geoCountryCode]);

  // #############################################################################
  // Store and language referer
  // #############################################################################
  /**
   * undefined - Not processed
   * null - Not found
   * StoreId - Found store
   */
  const [refererStoreId, setRefererStoreId] = useState<Nullish<StoreId>>(() => {
    // Initialize from localStorage to avoid initial undefined → value change
    const stored = localStorage.getItem(LocalStorageKey.StoreReferer);
    return evalStoreId(stored) || null;
  });
  const [refererLanguage, setRefererLanguage] = useState<Nullish<Language>>(
    () => {
      // Initialize from localStorage to avoid initial undefined → value change
      const stored = localStorage.getItem(LocalStorageKey.LanguageReferer);
      const language = evalLanguageCode(stored);
      // Sync cookie on initialization
      if (language) {
        saveCookie(COOKIE_MICROSITE_LANGUAGE, language.toUpperCase());
      }
      return language || null;
    },
  );

  // Track if we've already processed params to prevent infinite loop
  const hasProcessedParams = useRef(false);
  const lastProcessedSearch = useRef("");

  // Get the url/stored value
  useEffect(() => {
    // Allow reprocessing if search params actually changed
    if (location.search !== lastProcessedSearch.current) {
      hasProcessedParams.current = false;
      lastProcessedSearch.current = location.search;
    }

    // Prevent running on subsequent renders caused by history.replace
    if (hasProcessedParams.current) {
      return;
    }

    // Handle URL value
    const urlParams = new URLSearchParams(location.search);

    // Skip if no store param
    if (urlParams.has("s") || urlParams.has("l")) {
      // Read and parse the search param
      const urlStoreId = urlParams.get("s")?.toLowerCase();
      const urlLanguage = urlParams.get("l")?.toLowerCase();

      const evaluatedStoreId = evalStoreId(urlStoreId);
      const evaluatedLanguage = evalLanguageCode(urlLanguage);

      if (evaluatedStoreId) {
        setRefererStoreId(evaluatedStoreId);

        // Add the value to local storage
        localStorage.setItem(LocalStorageKey.StoreReferer, evaluatedStoreId);
      }

      if (
        (evaluatedLanguage &&
          evaluatedStoreId &&
          UISP_SITE_CONFIG.regions[evaluatedStoreId].languages.includes(
            evaluatedLanguage,
          )) ||
        (!evaluatedStoreId &&
          UISP_SITE_CONFIG.defaultRegion.languages.includes(evaluatedLanguage))
      ) {
        setRefererLanguage(evaluatedLanguage);

        // Add the value to local storage
        localStorage.setItem(
          LocalStorageKey.LanguageReferer,
          evaluatedLanguage,
        );

        // Override preference cookie as well
        saveCookie(COOKIE_MICROSITE_LANGUAGE, evaluatedLanguage.toUpperCase());
      }

      // Remove the search param
      urlParams.delete("s");
      urlParams.delete("l");

      // Replace the search params
      const searchString = urlParams.toString();
      const searchStringFragment = searchString
        ? `?${urlParams.toString()}`
        : "";
      const hashFragment = location.hash;
      const newUrl = [
        location.pathname,
        searchStringFragment,
        hashFragment,
      ].join("");

      // Only replace if the URL actually changed
      const currentUrl = location.pathname + location.search + location.hash;
      if (newUrl !== currentUrl) {
        hasProcessedParams.current = true;
        setTimeout(() => {
          history.replace(newUrl);
        }, 0);
      }

      return;
    }

    // Mark as processed since we handled localStorage in useState initializer
    hasProcessedParams.current = true;
  }, [location.pathname, location.search, location.hash]);

  const newDesignRoute = useRouteMatch<{ storeId?: string }>(
    REGIONAL_MICROSITE_PREFIX_PATH,
  );
  const routeStoreId = newDesignRoute?.params?.storeId;

  // #############################################################################
  // Parse final store
  // #############################################################################
  const store = useMemo(() => {
    // 1. Route matched
    const routeStore =
      routeStoreId && STORE_INFO_MAPPING.find((el) => el.id === routeStoreId);

    if (routeStore) {
      return routeStore;
    }

    // 2. By referer store
    const refererStore =
      refererStoreId &&
      STORE_INFO_MAPPING.find((el) => el.id === refererStoreId);
    if (refererStore) {
      return refererStore;
    }

    // 3. Get geo country store
    const geoStore =
      countryCode &&
      STORE_INFO_MAPPING.find((el) => el.countries.includes(countryCode));

    if (geoStore) {
      return geoStore;
    }

    // 4. Geo should always fallback to US but just in case, always return US last
    return STORE_INFO_USA;
  }, [countryCode, refererStoreId, routeStoreId]);

  // #############################################################################
  // Language cookie
  // #############################################################################
  const languageCookie = useMemo(() => {
    const languageCookie = cookies.load(COOKIE_MICROSITE_LANGUAGE);

    // If user has not specifically set a language then do nothing
    if (!languageCookie) return;

    return evalLanguageCode(languageCookie);
  }, []);

  // #############################################################################
  // Language in URL
  // #############################################################################
  const routeMatch = useRouteMatch(
    // @TODO add LANGUAGE_PATH before :path
    `${REGIONAL_MICROSITE_PREFIX_PATH}/:path*`,
  );

  const languageUrl = routeMatch?.params["language"];
  const matchedRestOfPath = routeMatch?.params["path"];

  // #############################################################################
  // Language
  // #############################################################################
  const language = useMemo(() => {
    return languageUrl ?? refererLanguage ?? languageCookie ?? DEFAULT_LANGUAGE;
  }, [languageCookie, languageUrl, refererLanguage]);

  /**
   * Update the language in the URL if it does not match the language in the
   * cookie.
   */
  const hasUpdatedLanguageUrl = useRef(false);
  const updateLanguageInUrl = useCallback(() => {
    // Prevent multiple calls
    if (hasUpdatedLanguageUrl.current) return;

    /**
     * Exit early if there is no language in the URL. It is not necessary to
     * update the URL.
     */
    if (!languageUrl) return;

    /**
     * Exit early if there is no saved language in the cookie.
     */
    if (!languageCookie) return;

    /**
     * Exit early if the language in the URL matches the language in the cookie.
     */
    if (languageUrl === languageCookie) return;

    hasUpdatedLanguageUrl.current = true;
    const restOfPath = matchedRestOfPath
      ? pathnameToParts(matchedRestOfPath)
      : [];

    history.replace(partsToPathname([store.id, languageCookie, ...restOfPath]));
  }, [languageCookie, languageUrl, matchedRestOfPath, store.id]);

  /**
   * Run once to update the language in the URL if it does not match the language.
   */
  useEffectOnce(() => {
    updateLanguageInUrl();
  });

  // #############################################################################
  // Create full store urls
  // #############################################################################
  const routes = useMemo(() => {
    return allRoutes(store.id, language);
  }, [store.id, language]);

  // #############################################################################
  // Routing util
  // #############################################################################
  const onLinkClick = useCallback((e, destinationUrl) => {
    // Navigate to the pathname locally
    const url = new URL(destinationUrl);
    const currentUrl = new URL(window.location.href);

    if (
      url.hostname === currentUrl.hostname ||
      currentUrl.hostname === "localhost"
    ) {
      e.preventDefault();

      if (url.hash && url.pathname === currentUrl.pathname) {
        history.replace({ hash: url.hash });
      } else {
        history.push({ pathname: url.pathname, hash: url.hash });
      }

      if (url.hash) {
        const element = document.getElementById(url.hash.replace(/^#/, ""));
        if (element) {
          const sectionOffsetTop = element.offsetTop;

          window.scrollTo({
            top: sectionOffsetTop,
            behavior: "smooth",
          });
        }
      }
    }
  }, [history]);

  /**
   * Util to get root url for default store redirect
   */
  // const getDefaultStoreLanguageRoute = useCallback(() => {
  //   const regionCode = geoCountryCode || DEFAULT_COUNTRY_CODE;

  //   // NOTE: Currently in prod we only want single language! Remove this if-statement when launching translations
  //   if (process.env.isEnvProduction) {
  //     return `/${store.id}/${DEFAULT_LANGUAGE}`;
  //   }

  //   const defaultLanguage = getDefaultLanguageByRegion(regionCode);
  //   return `/${store.id}/${defaultLanguage}`;
  // }, [geoCountryCode, store.id]);

  // #############################################################################
  // Embed
  // #############################################################################
  const isAppView = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);

    return urlParams.get("appview") === "true";
  }, [location.search]);
  // #############################################################################

  // #############################################################################
  // Result
  // #############################################################################
  const contextValue = useMemo(
    () => ({
      store,
      routes,
      language,
      geoCountryCode,
      isAppView,
      onLinkClick,
    }),
    [store, routes, language, geoCountryCode, isAppView, onLinkClick],
  );

  return (
    <SiteSettings.Provider value={contextValue}>
      {children}
    </SiteSettings.Provider>
  );
};

export function useSiteSettings() {
  const context = useContext(SiteSettings);
  if (context === null) {
    throw new Error("useSiteSettings must be used within a SiteSettings");
  }
  return context;
}
